import { useState } from "react";
import { ClipboardList } from "lucide-react";

const COUNTS = [1, 2, 3, 4, 5, 6, 7];
const MINUTES = [10, 15, 20, 30, 45, 60];
const WEEKLY = "weekly";
const minutesLabel = (value) => (value === 60 ? "1 h" : `${value} min`);

function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" });
}

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function inDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return isoLocal(d);
}

export function planSummary(plan) {
  const weeks = plan.repeatWeeks || 1;
  const perWeek = Math.max(1, Math.round((plan.targetCount || 1) / weeks));
  let text = `${perWeek} séance${perWeek > 1 ? "s" : ""}`;
  if (plan.suggestedMinutes) text += plan.suggestedMinutes === 60 ? " d’une heure" : ` de ${plan.suggestedMinutes} minutes`;
  if (plan.recurring) text += " chaque semaine";
  else if (weeks > 1) text += ` par semaine, ${weeks} semaines (${plan.targetCount} au total)`;
  if (plan.dueDate) text += ` avant ${dayLabel(plan.dueDate)}`;
  return text;
}

/**
 * F3: what to practise, how many sessions, how long, before when.
 * No time slot is asked. Step 2 is a recap with "Proposer".
 */
const PlanFormCard = ({ time, mobileType = false, childName = "", self = false, initial = null, onSubmit, onStep, onCancel }) => {
  const [title, setTitle] = useState(initial?.title || "");
  const [instructions, setInstructions] = useState(initial?.instructions || "");
  const [count, setCount] = useState(
    initial ? Math.max(1, Math.min(7, Math.round(initial.targetCount / (initial.repeatWeeks || 1)))) : 3
  );
  const [minutes, setMinutes] = useState(initial?.suggestedMinutes || 20);
  const [dueDate, setDueDate] = useState(initial ? (initial.recurring ? WEEKLY : initial.dueDate || "") : WEEKLY);
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const text = mobileType ? "text-[15px]" : "text-[13px]";
  const label = `mt-3 block font-bold uppercase tracking-wider text-[#189346] ${mobileType ? "text-[12px]" : "text-[11px]"}`;
  const field = `mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 ${text} outline-none focus:border-[#25D366]`;
  const chip = (on) =>
    `min-w-[2.5rem] rounded-full border px-3 py-1.5 font-semibold ${mobileType ? "text-[15px]" : "text-[13px]"} ${
      on ? "border-[#25D366] bg-[#25D366] text-[#0a2914]" : "border-gray-200 bg-white text-[#2d5a3d]"
    }`;
  const body = {
    title: title.trim(),
    instructions: instructions.trim(),
    suggestedMinutes: minutes,
    dueDate: dueDate && dueDate !== WEEKLY ? dueDate : null,
    recurring: dueDate === WEEKLY,
  };
  const oneWeek = inDays(7);
  const twoWeeks = inDays(14);
  const weeks = dueDate === twoWeeks || (initial?.repeatWeeks === 2 && dueDate === initial.dueDate) ? 2 : 1;
  body.targetCount = count * weeks;
  body.repeatWeeks = body.recurring ? null : weeks;
  const dueChoices = [
    [WEEKLY, "Chaque semaine"],
    [oneWeek, "Dans 1 semaine"],
    [twoWeeks, "Dans 2 semaines"],
    ["", "Pas d’échéance"],
  ];

  const next = (event) => {
    event.preventDefault();
    setError("");
    if (body.title.length < 2) return setError("Écrivez en quelques mots ce qu’il faut pratiquer.");
    setReview(true);
    onStep?.(1);
    return undefined;
  };

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      await onSubmit(body);
      setDone(true);
      onStep?.(2);
    } catch (problem) {
      setError(problem.message || "Envoi impossible.");
      if (problem.status === 409) setBlocked(true);
    } finally {
      setBusy(false);
    }
  };

  const verb = initial ? "Envoyer" : self ? "Créer" : "Proposer";
  return (
    <div className="w-[94%] sm:w-[88%] max-w-[23rem] self-start rounded-2xl rounded-tl-sm bg-white p-2.5 sm:p-3 shadow-sm" data-testid="plan-form-card">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#E9FBF0] text-[#189346]">
          <ClipboardList className="h-4 w-4" />
        </span>
        <p className={`font-semibold text-[#122118] ${text}`}>
          {self ? "Nouveau plan d’étude" : initial ? "Modifier la proposition" : `Plan d’étude pour ${childName || "votre enfant"}`}
        </p>
      </div>
      {cancelled ? (
        <p className={`mt-3 text-[#829C8D] ${text}`} data-testid="plan-form-cancelled">Annulé</p>
      ) : done ? (
        <p className={`mt-3 text-[#2d5a3d] ${text}`} data-testid="plan-form-done">✓ {body.title} · {planSummary(body)}</p>
      ) : review ? (
        <div className="mt-3" data-testid="plan-form-review">
          <p className={`font-semibold text-[#122118] ${text}`}>{body.title}</p>
          <p className={`text-[#4a5f53] ${text}`}>{planSummary(body)}</p>
          {body.instructions && <p className={`mt-1 text-[#4a5f53] ${mobileType ? "text-[14px]" : "text-[12px]"}`}>« {body.instructions} »</p>}
          {error && <p className="mt-2 text-xs text-[#9b3b3b]" data-testid="plan-form-error">{error}</p>}
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => { setReview(false); setBlocked(false); setError(""); onStep?.(0); }} className={`flex-1 rounded-full border border-gray-200 px-3 py-2 font-semibold text-[#2d5a3d] ${text}`} data-testid="plan-form-back">
              Modifier
            </button>
            {!blocked && (
              <button type="button" onClick={send} disabled={busy} className={`flex-1 rounded-full bg-[#25D366] px-3 py-2 font-semibold text-[#0a2914] disabled:opacity-50 ${text}`} data-testid="plan-form-submit">
                {busy ? "Envoi…" : verb}
              </button>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={next}>
          <label className={label} htmlFor="plan-title">Quoi pratiquer</label>
          <input id="plan-title" data-testid="plan-form-title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="Exercices sur les fractions" className={field} />
          <label className={label} htmlFor="plan-instructions">Consigne (facultatif)</label>
          <input id="plan-instructions" data-testid="plan-form-instructions" value={instructions} maxLength={1000} onChange={(e) => setInstructions(e.target.value)} placeholder="Ex. : cahier p. 42" className={field} />
          <p className={label}>Séances par semaine</p>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="plan-form-count">
            {COUNTS.map((value) => (
              <button key={value} type="button" className={chip(count === value)} onClick={() => setCount(value)}>{value}</button>
            ))}
          </div>
          <p className={label}>Durée d’une séance</p>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="plan-form-minutes">
            {MINUTES.map((value) => (
              <button key={value} type="button" className={chip(minutes === value)} onClick={() => setMinutes(value)}>{minutesLabel(value)}</button>
            ))}
          </div>
          <p className={label}>Avant quand</p>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="plan-form-due">
            {dueChoices.map(([value, text]) => (
              <button key={text} type="button" className={chip(dueDate === value)} onClick={() => setDueDate(value)}>
                {text}
              </button>
            ))}
          </div>
          {dueDate && dueDate !== WEEKLY && <p className={`mt-1 text-[#4a5f53] ${mobileType ? "text-[14px]" : "text-[12px]"}`}>Avant {dayLabel(dueDate)}</p>}
          {error && <p className="mt-2 text-xs text-[#9b3b3b]" data-testid="plan-form-error">{error}</p>}
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => { setCancelled(true); onCancel?.(); }} className={`flex-1 rounded-full border border-gray-200 px-3 py-2 font-semibold text-[#2d5a3d] ${text}`} data-testid="plan-form-cancel">
              Annuler
            </button>
            <button type="submit" className={`flex-[2] rounded-full bg-[#25D366] px-4 py-2 font-semibold text-[#0a2914] ${text}`} data-testid="plan-form-next">
              Continuer
            </button>
          </div>
        </form>
      )}
      <div className="mt-1 text-right text-[10px] text-gray-500">{time}</div>
    </div>
  );
};

export default PlanFormCard;
